/**
 * The chrome every dialog owes, in ONE place (WS-Z7 / R1).
 *
 * The inventory found four modal-and-drawer vocabularies. Two survive — `ui/Modal.tsx`
 * (centred) and `ui/Drawer.tsx` (right-side slide-over) — and they differ only in where the
 * panel sits. Everything an operator can actually feel is the same contract, so it is written
 * once here rather than twice and drifting:
 *
 *   • focus moves INTO the dialog on open, and back to whatever opened it on close;
 *   • Tab is trapped inside the panel (last wraps to first, Shift+Tab back);
 *   • Escape closes;
 *   • the heading is the dialog's accessible name (`aria-labelledby`), because
 *     `aria-modal` with no name announces the word "dialog" and nothing else.
 *
 * The overlay click is the third close path. It stays a real element with a real handler in the
 * markup, but the handler is `onOverlayClick` from here, so all three paths answer to the same
 * rule about WHICH dialog they belong to.
 *
 * STACKING (review-1 F-1): dialogs nest — the `bare` Modal exists for a viewer opened from
 * inside a Drawer. Only the TOP-most open dialog answers Escape, traps Tab and accepts an
 * overlay click; every dialog under it is inert until it is top again. Without that, each hook
 * put its own unconditional listener on `document`, sibling listeners on one target all run,
 * and one Escape closed the whole stack.
 *
 * ONE DECISION PER EVENT (review-2 F-9): asking "am I top?" once per listener is not enough.
 * React flushes a discrete event's state update SYNCHRONOUSLY between two `document`
 * listeners, so the moment the top dialog's `onClose` unmounts it, the next listener on the
 * SAME keypress re-evaluates, finds itself top, and closes too — and that is the ordinary
 * shape, because React runs a child's effect first, so a Modal inside a Drawer registers
 * first and the Drawer's listener runs second. The keypress is therefore latched: the first
 * dialog that establishes it is top claims the event, and every later listener on it returns.
 * (The overlay path needs no latch — a click reaches one element.)
 */
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";

/**
 * Every open dialog, in the order its effect registered. Module-level because the question
 * "am I on top?" cannot be answered from inside one component.
 */
export interface DialogHandle {
  panel: RefObject<HTMLDivElement | null>;
  /**
   * The dialog this one is rendered INSIDE, down the React tree — `null` for a dialog on the
   * page itself, and for one in another React root (a context does not cross roots).
   */
  parent: DialogHandle | null;
}
const openDialogs: DialogHandle[] = [];

/**
 * Keydown events a dialog has already claimed (review-2 F-9).
 *
 * A `WeakSet` because the key is the event object itself: it is alive only while the browser
 * is dispatching it, and nothing here should keep it alive a moment longer.
 */
const claimedKeys = new WeakSet<Event>();

/**
 * The dialog whose subtree is being rendered — `null` on the page itself. Each dialog provides
 * ITSELF to everything it renders, so a dialog opened from inside it knows which dialog it is
 * inside (review-2 F-10, corrected by review-3 F-15). A React context, not DOM containment,
 * because a panel rendered through `createPortal` is not inside its opener's panel while a
 * context follows the React tree wherever the node is put.
 */
const DialogStackContext = createContext<DialogHandle | null>(null);

/**
 * Wraps a dialog's whole subtree so a dialog rendered inside it knows what it is inside.
 *
 * `createElement` rather than JSX because this file is the hook, not a component file, and the
 * two components that own the markup (`ui/Modal.tsx`, `ui/Drawer.tsx`) both wrap with it.
 */
export function DialogStackProvider({ dialog, children }: { dialog: DialogHandle; children: ReactNode }) {
  return createElement(DialogStackContext.Provider, { value: dialog }, children);
}

/**
 * A dialog's ANCESTRY, outermost first and ending in the dialog itself — `[d]` for one on the
 * page. This is the whole walk up `parent`, not one step of it: the chains of two dialogs in
 * the same tree have to diverge at the branch that separates them, and a single step would
 * compare a dialog against its own parent instead (review-5 F-23).
 */
function chainOf(dialog: DialogHandle): DialogHandle[] {
  const chain: DialogHandle[] = [];
  for (let e: DialogHandle | null = dialog; e !== null; e = e.parent) chain.unshift(e);
  return chain;
}

/**
 * The dialog the operator is actually looking at: THE ONE WHOSE ANCESTRY, READ ROOT-FIRST AS THE
 * ORDER EACH DIALOG WAS OPENED, IS GREATEST.
 *
 * Registration order alone cannot decide it: React runs a CHILD's effect before its parent's,
 * so a Modal rendered inside a Drawer registers FIRST even though it is on top. It was settled
 * by DOM containment first, which a `createPortal`ed panel escapes by definition (review-2
 * F-10), and then by a depth counted down a React context — but depth is a PARTIAL fact and
 * taking the maximum of it made a total order out of it (review-3 F-15): a shell's confirm
 * dialog lives in page state and renders as a SIBLING of the drawer, so it is one level deep
 * while the drawer's own modal is two, and the dialog the operator had just opened read as
 * underneath the one behind it — no caret, and Escape closing the wrong dialog.
 *
 * What decides it is PAINT order, because paint order is what the operator sees. `.drawer` and
 * `.modal` are `position: fixed` with a `z-index`, so every panel is a stacking context and
 * everything rendered inside it paints inside it. Two dialogs therefore rank at the point their
 * ancestries BRANCH — the sibling opened later is above, and its whole subtree comes with it —
 * and a dialog whose ancestry is a PREFIX of another's is the ancestor, so the descendant is
 * above it however the two registered.
 *
 * Both earlier rules were that comparison stopped short. "Later registration wins unless it is
 * an ancestor" consults ancestry only between the two dialogs being compared, so a dialog opened
 * inside a Drawer that a page-level dialog is already above took the top from it (review-4
 * F-19). Ranking by the ROOT fixes exactly that and leaves the identical defect one level down,
 * because collapsing a tree to one rank compares two dialogs in the SAME tree by registration
 * alone — even when one of them is buried under a sibling branch that opened later (review-5
 * F-22). The whole chain is the generalisation of both: it is the branch that ranks them,
 * wherever the branch is.
 *
 * Ancestry comes down the same context depth did, so a portal is still ordered by the React tree
 * and not by the DOM. A dialog in a SECOND React root, where the context does not reach, has no
 * ancestor: its chain is itself alone, and it ranks against everything else by when it opened.
 */
function topDialog(): DialogHandle | null {
  let top: DialogHandle | null = null;
  for (const entry of openDialogs) {
    if (top === null) {
      top = entry;
      continue;
    }
    const chainEntry = chainOf(entry);
    const chainTop = chainOf(top);
    const shared = Math.min(chainEntry.length, chainTop.length);
    let i = 0;
    while (i < shared && chainEntry[i] === chainTop[i]) i++;
    const above =
      i === shared
        ? // One chain is a PREFIX of the other, so one dialog is rendered inside the other and
          // the descendant is above it however the two registered. (A parent can register after
          // the child it contains when they mount in ONE commit — React runs a child's effect
          // first — which is the whole reason a rule was needed.)
          chainEntry.length > chainTop.length
        : // They branch, so the branch decides: the sibling opened later is above, and
          // everything inside it comes with it.
          openDialogs.indexOf(chainEntry[i]) > openDialogs.indexOf(chainTop[i]);
    if (above) top = entry;
  }
  return top;
}

/**
 * The open dialog whose panel holds `node`, or `null`. The INNERMOST one, which with nested
 * panels is the last match in registration order.
 */
function dialogHolding(node: Element | null): DialogHandle | null {
  if (node === null) return null;
  let held: DialogHandle | null = null;
  for (const entry of openDialogs) if (entry.panel.current?.contains(node)) held = entry;
  return held;
}

/**
 * Where the caret goes when it cannot go back to the control that opened the dialog
 * (review-2 F-12) — a drawer opened from a "Details" row that a background refresh dropped,
 * or a control that is still there but is now behind the overlay of a dialog still open.
 * Landing on `<body>` is a keyboard user starting over at the top of the document, so the
 * anchor is, in order: the dialog still open under this one, the `main` landmark, and `<body>`
 * only when there is neither.
 *
 * Call AFTER this dialog has left `openDialogs`, so `topDialog()` is the one that survives it.
 */
function focusFallbackAnchor(): HTMLElement {
  const anchor: HTMLElement =
    topDialog()?.panel.current ?? document.querySelector("main") ?? document.body;
  // `tabindex="-1"` makes a landmark or a panel focusable programmatically without putting it
  // in the Tab sequence. Only added when the element does not already say something itself.
  if (!anchor.hasAttribute("tabindex")) anchor.setAttribute("tabindex", "-1");
  anchor.focus();
  return anchor;
}

/**
 * Said out loud in DEV when the opener was DELETED and nothing explains it (review-2 F-12,
 * narrowed by review-3 F-16). An opener that went because the dialog holding it closed — the
 * close button of the dialog above, which a stack that mounted in one commit captures at every
 * level — is not something an adopter can keep mounted, and warning about it at every level of
 * an ordinary unwind trains people to ignore the message.
 */
function warnLostOpener(anchor: HTMLElement): void {
  if (import.meta.env.DEV) {
    console.warn(
      "useDialogChrome: the control that opened this dialog was removed from the document " +
        `while it was open, so focus went to <${anchor.tagName.toLowerCase()}> instead of back ` +
        "to it. Keep the opener mounted while its dialog is, or open the dialog from a control " +
        "that outlives the row.",
    );
  }
}

/** Everything focusable a dialog panel can hold, in DOM order. */
const FOCUSABLE =
  'a[href],area[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),' +
  'button:not([disabled]),iframe,object,embed,[tabindex]:not([tabindex="-1"]),[contenteditable="true"]';

function focusablesIn(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    // `offsetParent === null` is jsdom-hostile (it is always null there), so visibility is
    // read from the attributes instead: what a test can see is what a screen reader can.
    (el) => !el.hasAttribute("hidden") && el.getAttribute("aria-hidden") !== "true",
  );
}

export interface DialogChrome {
  /** Put on the panel element — the focus trap's boundary, and the stack's identity. */
  panelRef: RefObject<HTMLDivElement | null>;
  /** Put on the close button — where focus lands on open. */
  closeRef: RefObject<HTMLButtonElement | null>;
  /** Put on the heading, and pass as `aria-labelledby` on the `role="dialog"` node. */
  titleId: string;
  /**
   * Put on the overlay element. NOT `onClose` directly: an overlay belonging to a dialog that
   * something else is open on top of must not close it out from under the operator.
   */
  onOverlayClick: () => void;
  /**
   * This dialog's identity in the stack. Pass to `DialogStackProvider` around EVERYTHING the
   * component renders, so a dialog opened from inside this one is ordered above it — through a
   * portal too, which is the whole point (review-2 F-10).
   */
  dialog: DialogHandle;
}

export function useDialogChrome(onClose: () => void): DialogChrome {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const titleId = useId();
  // The dialog we are rendered inside, or `null` on the page itself. Stable for this dialog's
  // whole life: a dialog cannot be re-parented while it is open.
  const parent = useContext(DialogStackContext);
  // The element that had focus when the dialog opened. Captured in a ref during the FIRST
  // effect pass so the restore on unmount is the opener and not something the dialog focused.
  const openerRef = useRef<Element | null>(null);
  // The dialog the opener was rendered inside, if any. An opener that disappears because THAT
  // dialog closed is explained, and is not reported (review-3 F-16).
  const openerOwnerRef = useRef<DialogHandle | null>(null);
  // This dialog's identity in the stack. A ref, not the panel element, because the entry has to
  // exist before the panel does and has to survive every re-render unchanged.
  const entryRef = useRef<DialogHandle | null>(null);
  if (entryRef.current === null) entryRef.current = { panel: panelRef, parent };
  const entry = entryRef.current;
  /**
   * The LATEST `onClose`, read at close time rather than captured at open time (review-1 F-2).
   *
   * Every call site passes an inline arrow, so `onClose` has a new identity on every render of
   * the parent. Listing it as an effect dependency tore the dialog's chrome down and set it up
   * again on each of those renders, and the set-up focuses the close button — which yanked the
   * caret out of whatever the operator was typing into. What close DOES may change on every
   * render; what open DID must happen once.
   */
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  const onOverlayClick = useCallback(() => {
    if (topDialog() !== entry) return;
    onCloseRef.current();
  }, [entry]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    openerRef.current = document.activeElement;
    openerOwnerRef.current = dialogHolding(openerRef.current);
    openDialogs.push(entry);
    // Only the top-most takes the caret. A Drawer whose effect runs AFTER the Modal inside it
    // would otherwise pull focus straight back out of the dialog on top of it.
    if (topDialog() === entry) closeRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" && e.key !== "Tab") return;
      // Already answered by another dialog on THIS keypress (review-2 F-9). Without the latch
      // the check below is re-evaluated after the top dialog has unmounted itself, and the
      // dialog underneath reads as top and closes on the same Escape.
      if (claimedKeys.has(e)) return;
      // Inert while something is open above us — Escape and the Tab trap both belong to the top.
      if (topDialog() !== entry) return;
      // Left open on a page the shell has hidden: the visible page owns the keyboard.
      if (panelRef.current?.closest("[inert]")) return;
      // Top, so this keypress is ours whatever we do with it — including deciding to do
      // nothing (a Tab inside a panel that does not need to wrap).
      claimedKeys.add(e);
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      const panel = panelRef.current;
      if (!panel) return;
      const items = focusablesIn(panel);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      // Wrap at both ends, and pull focus back in when it has escaped the panel entirely
      // (browser chrome, a stray `autofocus`, or the document body after a re-render).
      if (e.shiftKey && (active === first || !panel.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const wasTop = topDialog() === entry;
      // Spliced by identity, never popped: dialogs unmount out of order (a drawer can close
      // while a modal it opened is still up), and a blind pop would drop the wrong one.
      const at = openDialogs.indexOf(entry);
      if (at !== -1) openDialogs.splice(at, 1);
      // A dialog that was NOT the top one must not pull the caret out of the one that is
      // (review-2 F-11): a drawer dropped by a background refresh used to yank focus out of
      // the modal above it and onto a page control behind both of them. By the time this
      // passive cleanup runs React has already detached our panel, so the caret has ALREADY
      // fallen to `<body>` if it was inside us — anything else still holding it belongs to
      // somebody who is still open, and closing underneath them is no reason to take it.
      const active = document.activeElement;
      const caretIsLoose = active === null || active === document.body;
      if (!wasTop && !caretIsLoose) return;
      const opener = openerRef.current;
      // Only restore focus somebody can still see. `HTMLElement.focus()` on a detached node
      // never throws — in a browser or in jsdom — so this guard is not about a crash
      // (review-2 F-12 corrects the comment that said it was): focusing a node that is no
      // longer in the document silently leaves the caret on `<body>`, which is where the
      // deliberate fallback below goes instead. And "somebody can still see it" is more than
      // being in the document: with another dialog still open, everything outside ITS panel is
      // behind a modal overlay, so the survivor takes the caret instead (review-3 F-16).
      const survivor = topDialog()?.panel.current ?? null;
      const openerIsReachable =
        opener instanceof HTMLElement && opener.isConnected && (survivor === null || survivor.contains(opener));
      if (openerIsReachable) {
        opener.focus();
        return;
      }
      const anchor = focusFallbackAnchor();
      // Report only a DELETED opener, and only when its deletion is the app's doing: an opener
      // that went with the dialog holding it is the ordinary shape of a stack closing down.
      const owner = openerOwnerRef.current;
      const ownerClosedToo = owner !== null && openDialogs.indexOf(owner) === -1;
      const openerWasDeleted = !(opener instanceof HTMLElement && opener.isConnected);
      if (openerWasDeleted && !ownerClosedToo) warnLostOpener(anchor);
    };
    // `entry` is a ref's contents and never changes, so this runs exactly ONCE per open.
  }, [entry]);

  return { panelRef, closeRef, titleId, onOverlayClick, dialog: entry };
}
